import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import CircularProgress from '@material-ui/core/CircularProgress'
import Popper from '@material-ui/core/Popper'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import Autocomplete from '@material-ui/lab/Autocomplete'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import debounce from 'lodash/debounce'
import { matchSorter } from 'match-sorter'

import type { PopperProps } from '@material-ui/core/Popper'
import type { UseAutocompleteProps } from '@material-ui/lab'
import type { MatchSorterOptions } from 'match-sorter'

import * as Model from 'api/model'
import PoziAPISearch from 'api/search'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import PoziNotifications from 'utils/notifications'

const useStyles = makeStyles({
  inputRoot: {
    backgroundColor: 'white',
    borderRadius: (props:SearchAutocomplete) => props.isWidget ? 'unset' : 4, // Remove border for widgets
    paddingBlock: props => props.isWidget ? '2px !important' : 6
  },
  listbox: {
    padding: 0
  },
  loading: {
    padding: props => props.isWidget ? '5px 10px' : '8px 10px'
  },
  option: {
    display: 'block',
    fontSize: 15,
    borderBottom: 'solid 0.5px rgb(230, 230, 230)',
    minHeight: 'inherit',
    paddingBottom: 0,
    paddingLeft: 10,
    paddingTop: 3,
    float: 'left',
    width: '100%',

    // Allow tooltips for disabled options
    '&.MuiAutocomplete-option[aria-disabled="true"]': {
      cursor: 'default',
      pointerEvents: 'auto'
    },
    '&.MuiAutocomplete-option[aria-disabled="true"]:active': {
      pointerEvents: 'none'
    },

    // Bit of a hack to style the search results
    '& .autocomplete-search-option': {},
    '& .autocomplete-search-option-value': {
      float: 'left'
    },
    '& .autocomplete-search-option-category': {
      fontSize: 8,
      textAlign: 'right',
      color: 'lightslategray',
      float: 'right',
      marginLeft: 10
    }
  },
  paper: {
    borderRadius: props => props.isWidget ? 'unset' : 4, // Remove border for widgets
    margin: 0
  }
})

function sleep (delay = 0) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay)
  })
}

export const useMountRef = () => {
  const ref = useRef(false)

  const unMount = () => {
    ref.current = false
  }

  useEffect(() => {
    ref.current = true
    return unMount
  }, [])

  return ref
}

export const observePoziModel = (path: string, fn: (...args: any[]) => any) => {
  // console.debug('observeModel register', path)
  const observerIndex = Model.on(path, fn)

  const unObserve = () => {
    // console.debug('observeModel unregister', path, observerIndex)
    Model.un(path, observerIndex)
  }

  useEffect(() => {
    // console.debug('observerModel unmount', path)
    return unObserve
  }, [])
}

// This replaces the Popper component for displaying the search results with
const WidgetPopperComponent = (props: PopperProps) => <div>{props.children as ReactNode}</div>

const SearchAutoComplete = (props: SearchAutocomplete) => {
  // State management
  const [inFocus, setInFocus] = useState(false)
  const [initialised, setInitialised] = useState(false) // keep track of component having been initialised

  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<SearchAutoCompleteOption[]>([])
  const [value, setValue] = useState('')

  const classes = useStyles(props)

  // Keep track of whether or not component is mounted.
  // This is to prevent memory leaks when async functions
  // try to access the component after it has been unmounted
  const mountRef = useMountRef()

  // Keep track of the search input node for focusing
  let searchTextField: HTMLElement

  const { autoFocus, isWidget, onClearInput, placeholder } = props

  // ////////////////////////////////////////////////////////////
  // Load once when component gets initialised
  // (like componentDidMount)
  useEffect(() => {
    // Get query value from model and set it as inputValue
    const query: string = Model.get('app.search.query', '')
    console.debug('SearchAutoComplete init', query)

    // if query has been pre-set in model, show this on start-up
    if (query) {
      onQueryChangeDebounced(query)
    }

    setInitialised(true)
  }, []) // empty array means 'run once'

  // Make sure that the input field receives focus on startup
  useEffect(() => {
    // console.debug('SearchAutoComplete initialised. Setting focus.', inFocus)

    setInFocus(true)
  }, [initialised])

  // ////////////////////////////////////////////////////////////
  // Listen to changes from elsewhere in the model for query

  // Run search when the search query stored in the model changes externally
  const onQueryChange = (query: string) => {
    // console.debug('SearchAutoComplete - search onQueryChange', `'${query}'`, `'${inputValue}'`)

    if (mountRef.current && query && query !== inputValue) {
      setInputValue(query)

      setOpen(true) // show results
      setInFocus(true)
    }
  }

  // Use 'useCallback' to avoid React recreating the function on every render
  // Use debounce to prevent too many calls in a short space of time
  const onQueryChangeDebounced = useCallback(debounce(onQueryChange, 300), [])

  // DISABLED FOR NOW
  // observePoziModel('app.search.query', onQueryChangeDebounced)

  // ////////////////////////////////////////////////////////////
  // Listen for changes in the model for search options
  const onSearchOptionsChange = (layerConf: ConfigLayerOptions) => {
    console.debug('SearchAutoComplete - onSearchOptionsChange', layerConf.titleid, `'${inputValue}'`, mountRef.current)

    if (mountRef.current) {
      // Clear input value (query) and focus on search field
      setInputValue('')
      if (autoFocus) {
        setInFocus(true)
      }
    }
  }

  // Use 'useCallback' to avoid React recreating the function on every render
  // Use debounce to prevent too many calls in a short space of time
  const onSearchOptionsChangeDebounced = useCallback(debounce(onSearchOptionsChange, 200), [])

  // Listen to changes in the search options
  // TODO: there is a side effect somewhere that causes this and potentially other functions
  // to be called more and more times, with every re-creation of this component
  observePoziModel('app.search.layer', onSearchOptionsChangeDebounced)

  // Determine whether or not to search
  const onInputValueChange = (inputValue: string) => {
    // Ideally we make it configurable after how many characters we will send a request
    // but for now we just set it to 1
    const inputValueIsNotEmpty = inputValue?.trim()?.length >= 1

    // Do not search if input value is empty and reset the search
    if (!inputValueIsNotEmpty) {
      // console.debug('SearchAutoComplete - onInputValueChange - shouldSearch', inputValueIsNotEmpty, inputValue, Model.get('app.search.query'), value)

      // Clear input value
      Model.set('app.search.query', '')

      // Callback function if provided (e.g. from WidgetWrapper)
      if (typeof onClearInput === 'function') {
        console.debug('SearchAutoComplete - onInputValueChange - calling onClearInput', inputValue)

        onClearInput.call(onClearInput, inputValue)
      }

      return
    }

    // Call the search function
    // Nifty trick to perform async calls within a useEffect
    (async () => {
      // Prevent memory leaks
      if (!mountRef.current) {
        return
      }

      // Show loading spinner
      setLoading(true)

      // Workaround for 'no results' not displaying
      const noResults = [
        {
          disabled: true,
          value: `No results found for '${inputValue}'`
        }
      ]

      try {
        // Store the query in the model so PoziAPISearch can use it
        Model.set('app.search.query', inputValue)

        // console.debug('onInputValueChange - Search query store in model', `'${inputValue}'`)

        // Debug sleep helper for delaying search results
        // await new Promise(resolve => setTimeout(resolve, 5 * 1000))

        // Do the actual search
        let results = await PoziAPISearch().search()

        // console.debug('SearchAutoComplete - onInputValueChange - results', results)

        // Show a 'no results' text when there are no results
        if (!results || results.length === 0) {
          results = noResults
        }

        if (mountRef.current) {
          setOptions(results)
        }
      } catch (error) {
        console.error('Search error', error)

        const errorMessage = (error instanceof Error) ? error.message : 'unknown error'

        PoziNotifications().warning(`Something went wrong with this search: ${errorMessage}`)
      } finally {
        if (mountRef.current) {
          setLoading(false)
        }
      }
    })()
  }

  // ////////////////////////////////////////////////////////////
  // Avoid too many lookups while the user is still typing
  const onInputValueChangeDebounced = useCallback(debounce(onInputValueChange, 500), [])

  useEffect(() => {
    // console.debug('Search inputValue/value change', inputValue, value)

    // Store query in model
    onInputValueChangeDebounced(inputValue)
  }, [inputValue, value])

  // ////////////////////////////////////////////////////////////
  // Clear results when input value is empty
  useEffect(() => {
    // console.debug('Search open/inputValue change', open, inputValue)
    if (!open && !inputValue) {
      setOptions([])
    }

    // The following can happen when a user presses the 'clear' button
    if (open && !inputValue && options.length > 0) {
      console.debug('Search closing results due to empty input value')

      setOpen(false)
    }
  }, [inputValue, open])

  // ////////////////////////////////////////////////////////////
  // Handle whether or not search field needs to be focussed
  useEffect(() => {
    // console.debug('SearchAutocomplete - search in focus?', inFocus)

    if (inFocus) {
      searchTextField.focus()
    } else {
      searchTextField.blur()
    }
  }, [inFocus])

  // The Popper component normally takes care of perfectly positioning the
  // search results but this fails inside (small) iframes. We replace it with
  // a simple div
  const PopperComponent = isWidget ? WidgetPopperComponent : Popper

  // Do not limit size of search results when we are running in widget mode
  const listboxProps = isWidget ? { style: { maxHeight: 'unset' }, padding: 0, placement: 'bottom' } : {}

  // console.debug('AutoComplete Search (re)render)')

  // Override some of the filter options, disable results sorting in particular
  const filterOptionsFn: UseAutocompleteProps<SearchAutoCompleteOption, undefined, undefined, true>['filterOptions'] = (filterOptions, filterOptionsState) => {
    const { inputValue } = filterOptionsState

    const matchSorterOptions: MatchSorterOptions<SearchAutoCompleteOption> = {
      keys: ['value'],
      sorter: a => a, // do not sort items
      threshold: matchSorter.rankings.NO_MATCH // always show item
    }

    return matchSorter(filterOptions, inputValue, matchSorterOptions)
  }

  return (
    <Autocomplete
      autoHighlight // does not work in MUI 4
      blurOnSelect={true}
      classes={classes}
      clearOnEscape
      debug={false} // useful for inspecting search results in dev tools
      freeSolo={true}
      filterOptions={filterOptionsFn}
      getOptionDisabled={(option) => option.disabled as boolean}
      getOptionLabel={(option) => typeof option.value === 'string' ? option.value : String(option.value) || inputValue}
      getOptionSelected={(option, value) => option.value === value.value}
      inputValue={inputValue}

      ListboxProps={listboxProps}

      loading={loading}
      noOptionsText={'No results found'}

      // Gets triggered when an option gets selected (i.e. clicked) or cleared
      onChange={(event, selectedOption, reason) => {
        console.debug('search onChange', reason, inputValue, selectedOption, { ...event })

        PoziAPISearch().getResults(selectedOption, isWidget)
      }}

      onClose={() => setOpen(false)}

      onInputChange={(event, newInputValue, reason) => {
        // console.debug('search onInputChange', reason, { ...event }, inputValue, newInputValue)

        // Prevent memory leaks
        if (!mountRef.current) {
          return
        }

        // Store selected value upon click (which causes an input change with reason 'reset')
        if (reason === 'reset') {
          setValue(newInputValue)
          return
        }

        setInputValue(newInputValue)

        // Automatically show search results when they arrive
        if (!open && newInputValue && newInputValue !== inputValue) {
          // console.debug('Search open results due to new input value')

          setOpen(true)
        }
      }}

      onOpen={() => { setOpen(true) }}
      open={open}
      options={options}
      PopperComponent={PopperComponent}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={placeholder}
          margin='none'
          name={'Some name'}
          variant="outlined"

          // Keep track of focus
          onBlur={() => { setInFocus(false) }}
          onFocus={() => { setInFocus(true) }}

          // Keep track of input field to control focus
          inputRef={(input) => {
            searchTextField = input
          }}

          inputProps={{
            ...params.inputProps,
            'data-lpignore': true // Hide LastPass icon
          }}

          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          size="small"
        />
      )}

      renderOption={(option, { inputValue }) => {
        const { category = '', tooltip = '', value } = option

        const matchOptions = {
          // findAllOccurrences: true,
          insideWords: true
        }

        const matches = match(String(value), inputValue, matchOptions)
        const parts = parse(String(value), matches)

        // console.debug('Autocomplete - renderOption', category, value, tooltip, option)

        let searchOptionHtml = (
          <div className="autocomplete-search-option">
            <div className="autocomplete-search-option-value">
              {/* { title } */}
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 'bold' : 'inherit' }}>
                  {part.text}
                </span>
              ))}
            </div>
            <div className="autocomplete-search-option-category">
              {category}
            </div>
          </div>
        )

        if (tooltip) {
          searchOptionHtml = (
            <Tooltip title={tooltip}>
              {searchOptionHtml}
            </Tooltip>
          )
        }

        return searchOptionHtml
      }}
    />
  )
}

SearchAutoComplete.defaultProps = {
  autoFocus: true,
  isWidget: false,
  placeholder: 'Start typing here...'
}

export default SearchAutoComplete

interface SearchAutocomplete {
  autoFocus: boolean
  isWidget? : boolean
  onClearInput? : any // function
  placeholder? : string
}

export interface SearchAutoCompleteOption {
  category?: string
  disabled?: boolean
  tooltip?: string
  value: string | number
}
